import { FC } from 'react';

import { Copy, X } from 'lucide-react';

import {
    autoPriceStatusText,
    BulkOfferAutoPriceStatus,
} from './BulkOfferAutoPriceStatus';
import { BulkOfferProductCell } from './BulkOfferProductCell';
import { BulkOfferPropertyField } from './BulkOfferPropertyField';
import { Button } from '../../../../components/ui/Button';
import { Checkbox } from '../../../../components/ui/Checkbox';
import { Input } from '../../../../components/ui/Input';
import { OfferDraft, OfferProperty } from '../../../../types';
import { cn } from '../../../../utils';
import {
    OfferDraftFormValues,
    resolveOfferPropertiesForProduct,
    validateOfferDraft,
} from '../__shared/offerDraft';
import { OfferPhotoUploader } from '../__shared/OfferPhotoUploader';

interface Props {
    offer: OfferDraft;
    offerProperties: OfferProperty[];
    tableProperties: OfferProperty[];
    selected: boolean;
    onSelectedChange: (selected: boolean) => void;
    onRemove: () => void;
    onDuplicate: () => void;
    onPhotoUploadingChange: (uploading: boolean) => void;
    autoPriceEnabled: boolean;
    onFieldChange: <Key extends keyof OfferDraftFormValues>(
        field: Key,
        value: OfferDraftFormValues[Key],
    ) => void;
}

const errorMessage = (error?: string | string[]) =>
    Array.isArray(error) ? error[0] : error;

export const BulkOfferTableRow: FC<Props> = ({
    offer,
    offerProperties,
    tableProperties,
    selected,
    onSelectedChange,
    onRemove,
    onDuplicate,
    onPhotoUploadingChange,
    autoPriceEnabled,
    onFieldChange,
}) => {
    const productName =
        offer.product?.name ?? offer.import_name ?? 'пропозиції';
    const propertiesForProduct = resolveOfferPropertiesForProduct(
        offer.product,
        offerProperties,
    );
    const draftErrors = validateOfferDraft(offer, offerProperties);
    const isReady = Object.keys(draftErrors).length === 0;
    const autoPriceHelperText = autoPriceStatusText(offer, autoPriceEnabled);
    const amountError = errorMessage(draftErrors.amount);
    const priceError = errorMessage(draftErrors.price);
    const propertyError = errorMessage(draftErrors.property_set);
    const photosError = errorMessage(draftErrors.photos);
    const helperText =
        offer.import_error ?? autoPriceHelperText ?? propertyError ?? null;
    const statusIcon = (
        <BulkOfferAutoPriceStatus
            offer={offer}
            autoPriceEnabled={autoPriceEnabled}
            ready={isReady}
        />
    );

    return (
        <div
            data-draft-id={offer.draft_id}
            data-testid="offer-draft-card"
            className={cn(
                'grid min-w-[var(--bulk-offers-min-width)] [grid-template-columns:var(--bulk-offers-grid)] items-start gap-2 px-3 py-2.5 transition-colors md:gap-3 md:px-4',
                !isReady && 'bg-destructive/5',
                selected && 'bg-primary/5',
            )}
        >
            <div className="flex h-9 items-center">
                <Checkbox
                    checked={selected}
                    aria-label={`Обрати ${productName}`}
                    onCheckedChange={(checked) =>
                        onSelectedChange(checked === true)
                    }
                />
            </div>

            <div className="min-w-0">
                <BulkOfferProductCell
                    product={offer.product}
                    importName={offer.import_name}
                    sourceCode={offer.source_code}
                    quantity={offer.amount}
                    statusIcon={statusIcon}
                />
                {helperText && (
                    <p className="mt-1 text-xs leading-snug text-muted-foreground">
                        {helperText}
                    </p>
                )}
            </div>

            <div className="min-w-0">
                <Input
                    id={`quantity-${offer.draft_id}`}
                    aria-label={`Кількість для ${productName}`}
                    aria-invalid={Boolean(amountError)}
                    type="number"
                    min="1"
                    value={offer.amount}
                    className={cn(
                        'h-9 min-w-0 text-sm',
                        amountError && 'border-red-600',
                    )}
                    onChange={(event) =>
                        onFieldChange('amount', event.target.value)
                    }
                />
                {amountError && (
                    <p className="mt-1 text-xs leading-snug text-red-600">
                        {amountError}
                    </p>
                )}
            </div>

            <div className="min-w-0">
                <div className="relative">
                    <Input
                        id={`price-${offer.draft_id}`}
                        aria-label={`Ціна для ${productName}`}
                        aria-invalid={Boolean(priceError)}
                        type="number"
                        step="1"
                        min="0"
                        value={offer.price}
                        className={cn(
                            'h-9 min-w-0 pr-7 text-sm',
                            priceError && 'border-red-600',
                        )}
                        onChange={(event) =>
                            onFieldChange('price', event.target.value)
                        }
                    />
                    <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sm text-muted-foreground">
                        ₴
                    </span>
                </div>
                {priceError && (
                    <p className="mt-1 text-xs leading-snug text-red-600">
                        {priceError}
                    </p>
                )}
            </div>

            {tableProperties.map((property) => (
                <BulkOfferPropertyField
                    key={property.slug}
                    columnProperty={property}
                    editableProperty={propertiesForProduct.find(
                        (candidate) => candidate.slug === property.slug,
                    )}
                    propertySet={offer.property_set}
                    inputId={`${property.slug}-${offer.draft_id}`}
                    ariaLabel={`${property.name} для ${productName}`}
                    onChange={(propertySet) =>
                        onFieldChange('property_set', propertySet)
                    }
                />
            ))}

            <div className="min-w-0">
                <OfferPhotoUploader
                    photos={offer.photos}
                    onChange={(photos) => onFieldChange('photos', photos)}
                    required={offer.product?.offer_photos_required}
                    error={photosError}
                    onUploadingChange={onPhotoUploadingChange}
                />
            </div>

            <div className="flex items-center justify-end gap-0.5">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Дублювати товар у черзі"
                    onClick={onDuplicate}
                >
                    <Copy className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    aria-label="Видалити товар із черги"
                    onClick={onRemove}
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
};
